import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { TutorialVideoWidgetComponent } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { TutorialVideoStep } from '../components/tutorial-video-widget/tutorial-video-widget.component';
import { ADMIN_TUTORIAL_FLOW } from '../components/tutorial-video-widget/tutorial-video-flows';
import { ManageProjectService } from '../services/manage-project.service';
import { CreateProjectService } from '../services/create-project.service';
import { AddProjectDialogComponent } from '../manage-project/add-project-dialog/add-project-dialog.component';
import { RoleViewService } from '../services/role-view.service';
import { NotificationService } from '../services/notification.service';

interface Project {
  id: string;
  projectName: string;
  modelYear: string;
  modelCode: string;
  projectCode: string;
  colorCode: string;
  colorName: string;
  namingMode: 'standard' | 'custom';
  dateCreated: string;
  isDeleted: boolean;
}

interface ProjectGroup {
  colorCode: string;
  colorName: string;
  projects: Project[];
  isExpanded?: boolean;
}

@Component({
  selector: 'app-manage-project',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatExpansionModule, TutorialVideoWidgetComponent],
  templateUrl: './manage-project.component.html',
  styleUrl: './manage-project.component.css',
  animations: [
    trigger('groupListAnimation', [
      state('collapsed', style({ opacity: 0, transform: 'translateY(8px)' })),
      state('expanded', style({ opacity: 1, transform: 'translateY(0)' })),
      transition('collapsed => expanded', animate('250ms ease-out')),
      transition('expanded => collapsed', animate('150ms ease-in'))
    ])
  ]
})
export class ManageProjectComponent implements OnInit {
  readonly adminTutorialSequence: TutorialVideoStep[] = ADMIN_TUTORIAL_FLOW;
  projects: Project[] = [];
  filteredProjects: Project[] = [];
  groupedProjects: ProjectGroup[] = [];
  activeTab: 'active' | 'deleted' = 'active';
  searchTerm = '';
  userRole = '';
  workDayId = '';
  userName = '';
  scrHeight = '';
  showDeleteConfirmModal = false;
  deleteConfirmTitle = '';
  deleteConfirmCallback: (() => void) | null = null;

  @HostListener('window:resize', ['$event'])
  getScreenSize(_event?: Event) {
    this.scrHeight = `${window.innerHeight - 200}px`;
  }

  constructor(private readonly dialog: MatDialog, private readonly manageProjectService: ManageProjectService,
    public readonly createProjectService: CreateProjectService, private readonly notificationService: NotificationService,
    public readonly roleviewservice: RoleViewService
  ) { }


  ngOnInit(): void {
    this.getScreenSize();
    this.getUserInfo();
    this.loadSampleProjects();
    this.filterProjects();
  }

  getUserInfo() {
    this.workDayId = this.roleviewservice.getWorkdayId();
    this.userName = this.roleviewservice.getUserName();
  }

  loadSampleProjects(): void {
    // Sample existing projects
    this.manageProjectService.fetchProject().subscribe({
      next: res => {
        this.projects = res.payload.map((item: any): Project => ({
          id: item.project_id?.toString(),
          projectName: item.project_name,
          modelYear: item.model_year,
          modelCode: item.model_code,
          projectCode: item.project_code,
          colorCode: item.color_code,
          colorName: item.color_name,
          namingMode: item.project_name_type,
          dateCreated: new Date(item.created_at).toISOString(),
          isDeleted: item.is_deleted
        }));
        // Show most recently created projects first
        this.projects.sort((a, b) => b.dateCreated > a.dateCreated ? 1 : -1);
        console.info(this.projects)
        this.manageProjectService.setFetchProject(this.projects);
        this.filterProjects()
      },
      error: (error: any) => {
        console.trace('Error loading projects:', error);
        this.notificationService.showError('Error loading projects from server');
      }
    })
  }

  openAddProjectDialog(): void {
    const dialogRef = this.dialog.open(AddProjectDialogComponent, {
      width: '550px',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'custom-dialog-container',
      data: { projects: this.projects, mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload = {
          m_year: result.modelYear,
          m_code: result.modelCode,
          p_code: result.projectCode,
          p_name: result.projectName,
          p_n_type: result.namingMode,
          colorCode: result.colorCode,
          colorName: result.colorName,
          createdBy: this.userName
        }

        this.manageProjectService.createProject(this.workDayId, payload).subscribe({
          next: (response) => {
            console.info("Project created", response);
            this.notificationService.showSuccess('Project created successfully!');
            // Reload projects from server after successful creation
            this.loadSampleProjects();
          },
          error: (error) => {
            console.trace('Error creating project', error);
            this.notificationService.showError('Failed to create project. Please try again.');
          }
        });
      }
    });
  }

  deleteProject(projectId: string): void {
    const id = this.projects.find(p => p.id === projectId);
    console.info("Deleting project", id);
    this.deleteConfirmTitle = id?.projectName || '';
    const payload = {
      projectId: projectId,
      isDeleted: true,
      updatedBy: this.userName
    };
    this.deleteConfirmCallback = () => {
      this.manageProjectService
        .deleteRestoreProject(this.workDayId, payload)
        .subscribe({
          next: () => {
            const foundProject = this.projects.find(p => p.id === projectId);
            if (foundProject) {
              foundProject.isDeleted = true;
              this.filterProjects();
            }
            this.notificationService.showSuccess('Project archived successfully!');
          },
          error: (err) => {
            console.trace('Error archiving project', err);
            this.notificationService.showError('Failed to archive project. Please try again.');
          }
        });
    };
    this.showDeleteConfirmModal = true;
  }

  confirmDelete(): void {
    if (this.deleteConfirmCallback) {
      this.deleteConfirmCallback();
    }
    this.cancelDelete();
  }

  cancelDelete(): void {
    this.showDeleteConfirmModal = false;
    this.deleteConfirmTitle = '';
    this.deleteConfirmCallback = null;
  }

  restoreProject(projectId: string): void {
    const payload = {
      projectId: projectId,
      isDeleted: false,
      updatedBy: this.userName
    };
    this.manageProjectService
      .deleteRestoreProject(this.workDayId, payload)
      .subscribe({
        next: () => {
          const project = this.projects.find(p => p.id === projectId);
          if (project) {
            project.isDeleted = false;
            this.filterProjects();
          }
          this.notificationService.showSuccess('Project restored successfully!');
        },
        error: (err) => {
          console.trace('Error restoring project', err);
          this.notificationService.showError('Failed to restore project. Please try again.');
        }
      });
  }

  editProject(project: Project): void {
    console.info("Editing project", project);
    const dialogRef = this.dialog.open(AddProjectDialogComponent, {
      width: '550px',
      maxHeight: '90vh',
      disableClose: false,
      panelClass: 'custom-dialog-container',
      data: { projects: this.projects, mode: 'edit', project }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload = {
          m_year: result.modelYear,
          m_code: result.modelCode,
          p_code: result.projectCode,
          p_name: result.projectName,
          p_n_type: result.namingMode,
          colorCode: result.colorCode,
          colorName: result.colorName,
          updatedBy: this.userName,
          projectId: this.projects.find(p => p.id === project.id)?.id
        };

        this.manageProjectService.updateProject(this.workDayId, payload).subscribe({
          next: () => {
            Object.assign(project, result);
            this.filterProjects();
            this.notificationService.showSuccess('Project updated successfully!');
          },
          error: (err) => {
            console.trace('Error updating project', err);
            this.notificationService.showError('Failed to update project. Please try again.');
          }
        });
      }
    });
  }

  getActiveProjects(): Project[] {
    return this.projects.filter(p => !p.isDeleted);
  }

  getDeletedProjects(): Project[] {
    return this.projects.filter(p => p.isDeleted);
  }

  onSearch(): void {
    this.filterProjects();
  }

  filterProjects(): void {
    let projects = this.activeTab === 'active' ? this.getActiveProjects() : this.getDeletedProjects();

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      projects = projects.filter(p => {
        const matches = p.projectName.toLowerCase().includes(term);
        const yearMatches = p.modelYear && p.modelYear !== 'N/A' && p.modelYear.includes(term);
        const codeMatches = p.modelCode && p.modelCode !== 'N/A' && p.modelCode.toLowerCase().includes(term);
        const projectCodeMatches = p.projectCode && p.projectCode !== 'N/A' && p.projectCode.toLowerCase().includes(term);
        return matches || yearMatches || codeMatches || projectCodeMatches;
      });
    }

    this.filteredProjects = projects;
    this.groupProjectsByColor();
  }

  groupProjectsByColor(): void {
    const groups: { [key: string]: ProjectGroup } = {};

    this.filteredProjects.forEach(project => {
      if (!groups[project.colorCode]) {
        groups[project.colorCode] = {
          colorCode: project.colorCode,
          colorName: project.colorName,
          projects: [],
          // Auto-expand groups only when a search term is active
          isExpanded: !!this.searchTerm
        };
      }
      groups[project.colorCode].projects.push(project);
    });

    this.groupedProjects = Object.values(groups);
  }

}

